import { create } from 'zustand'
import { createElement, createImageElement, defaultVariableWidth } from './defaults'
import { clampGridRange, coerceGridValue, emptyGridValue, gridCellValue, normalizeGridRange, type GridFillDirection, type GridRange } from './data-grid'
import type { CsvImportCommit } from './csv-import'
import { migrateProject } from './migration'
import type {
  AlignmentMode,
  CardRow,
  DeckProject,
  DeckVariable,
  DistributionMode,
  DropPosition,
  ElementGroup,
  ElementPatch,
  ElementType,
  ProjectAsset,
  ProjectFontFamily,
  RenderSettings,
  VariableType,
  VisualElement,
  WorkspaceTab,
} from './types'
import { newId } from './utils'

type ElementPatches = Record<string, ElementPatch>

interface EditorState {
  project: DeckProject | null
  activeTab: WorkspaceTab
  selectedElementId: string | null
  selectedElementIds: string[]
  selectedRowId: string | null
  dirty: boolean
  past: DeckProject[]
  future: DeckProject[]
  clipboard: VisualElement[]
  setProject: (project: DeckProject | null) => void
  setActiveTab: (tab: WorkspaceTab) => void
  setSelectedElement: (id: string | null) => void
  setSelectedElements: (ids: string[]) => void
  selectElement: (id: string, additive?: boolean) => void
  setSelectedRow: (id: string | null) => void
  markSaved: () => void
  renameProject: (name: string) => void
  updateCard: (patch: Partial<DeckProject['card']>) => void
  updateDataSettings: (patch: Partial<DeckProject['dataSettings']>) => void
  updateRender: (patch: Partial<RenderSettings>) => void
  addElement: (type: ElementType) => void
  addImageElement: (assetId: string, name?: string) => void
  updateElement: (id: string, patch: ElementPatch) => void
  updateElements: (patches: ElementPatches) => void
  deleteElement: (id: string) => void
  deleteSelected: () => void
  duplicateElement: (id: string) => void
  duplicateSelected: () => void
  copySelected: () => void
  pasteClipboard: () => void
  moveElement: (id: string, direction: -1 | 1) => void
  reorderElement: (sourceId: string, targetId: string, position: DropPosition) => void
  groupSelected: () => void
  ungroupSelected: () => void
  alignSelected: (mode: AlignmentMode) => void
  distributeSelected: (mode: DistributionMode) => void
  nudgeSelected: (dx: number, dy: number) => void
  addVariable: (type: VariableType, name?: string, defaultValue?: string | number) => string
  updateVariable: (id: string, patch: Partial<DeckVariable>) => void
  deleteVariable: (id: string, replacement?: string | number) => void
  addRow: () => void
  updateRow: (id: string, patch: Partial<CardRow>) => void
  updateCell: (rowId: string, variableId: string, value: string | number) => void
  setImageCell: (rowId: string, variableId: string, assetId: string, asset?: ProjectAsset) => void
  duplicateRow: (id: string) => void
  duplicateRows: (ids: string[]) => string[]
  deleteRow: (id: string) => void
  deleteRows: (ids: string[]) => void
  reorderRow: (sourceId: string, targetId: string, position: DropPosition) => void
  setRowOrder: (ids: string[]) => void
  replaceData: (variables: DeckVariable[], rows: CardRow[]) => void
  importData: (commit: CsvImportCommit) => void
  pasteGrid: (startRow: number, startColumn: number, cells: string[][]) => void
  pasteGridRows: (rowIds: string[], startColumn: number, cells: string[][], appendRows?: boolean) => number
  clearGrid: (range: GridRange) => void
  clearGridRows: (rowIds: string[], left: number, right: number) => void
  fillGrid: (range: GridRange, direction: GridFillDirection) => void
  fillGridRows: (rowIds: string[], left: number, right: number, direction: GridFillDirection) => void
  fillGridSeries: (range: GridRange, step?: number) => void
  fillGridSeriesRows: (rowIds: string[], column: number, step?: number) => void
  addAsset: (asset: ProjectAsset) => void
  addFontFamilies: (families: ProjectFontFamily[]) => void
  deleteFontFamily: (id: string) => void
  deleteFontFace: (familyId: string, faceId: string) => void
  undo: () => void
  redo: () => void
}

// Keep binary data-URL strings outside structuredClone's serialization path.
// They are immutable, while the surrounding asset/font metadata still needs
// independent objects for undo and redo snapshots.
export const snapshotProject = (project: DeckProject) => {
  const assets = project.assets
  const fonts = project.fonts
  const cloned = structuredClone({ ...project, assets: {}, fonts: {} }) as DeckProject
  cloned.assets = Object.fromEntries(Object.entries(assets).map(([id, asset]) => [id, { ...asset }]))
  cloned.fonts = Object.fromEntries(Object.entries(fonts).map(([id, family]) => [id, {
    ...family,
    faces: family.faces.map((face) => ({ ...face })),
  }]))
  return cloned
}

const snapshot = snapshotProject

const removeUnusedGroups = (project: DeckProject) => {
  const used = new Set(project.elements.map((element) => element.groupId).filter(Boolean))
  project.groups = project.groups.filter((group) => used.has(group.id))
}

const valuesForVariables = (variables: DeckVariable[]) =>
  Object.fromEntries(variables.map((variable) => [variable.id, variable.defaultValue]))

const assignGridValue = (project: DeckProject, row: CardRow, column: number, value: string | number) => {
  if (column === 0) row.name = String(value)
  else {
    const variable = project.variables[column - 1]
    if (variable) row.values[variable.id] = coerceGridValue(value, variable, project.assets)
  }
}

const bindingConstantValue = (current: unknown, replacement: string | number) => {
  if (typeof current === 'boolean') {
    if (typeof replacement === 'number') return Boolean(replacement)
    return /^(true|yes|1|on)$/i.test(replacement.trim())
  }
  if (typeof current === 'number') {
    const value = Number(replacement)
    return Number.isFinite(value) ? value : current
  }
  if (typeof current === 'string') return String(replacement)
  return replacement
}

const applyBindingConstant = (element: VisualElement, property: string, replacement: string | number) => {
  const segments = property.split('.')
  let target: unknown = element
  for (const [index, segment] of segments.entries()) {
    const last = index === segments.length - 1
    if (Array.isArray(target)) {
      target = target.find((candidate) => candidate && typeof candidate === 'object' && String((candidate as { id?: unknown }).id) === segment)
      if (!target) return
    } else if (target && typeof target === 'object') {
      const record = target as Record<string, unknown>
      if (last) record[segment] = bindingConstantValue(record[segment], replacement)
      else target = record[segment]
    } else return
  }
}

export const useEditorStore = create<EditorState>((set, get) => {
  const mutate = (recipe: (draft: DeckProject) => void) => {
    set((state) => {
      if (!state.project) return state
      const current = snapshot(state.project)
      const draft = snapshot(state.project)
      recipe(draft)
      draft.updatedAt = new Date().toISOString()
      return {
        ...state,
        project: draft,
        dirty: true,
        past: [...state.past.slice(-49), current],
        future: [],
      }
    })
  }

  const selectIds = (ids: string[]) =>
    set({ selectedElementIds: ids, selectedElementId: ids.length === 1 ? ids[0] : null })

  const duplicateIds = (sourceIds: string[]) => {
    const created: string[] = []
    mutate((draft) => {
      const sources = draft.elements.filter((element) => sourceIds.includes(element.id))
      const groupMap = new Map<string, string>()
      for (const source of sources) {
        if (source.groupId && !groupMap.has(source.groupId)) {
          const id = newId('group')
          groupMap.set(source.groupId, id)
          const oldGroup = draft.groups.find((group) => group.id === source.groupId)
          draft.groups.push({ id, name: `${oldGroup?.name ?? 'Group'} copy` })
        }
        const id = newId('element')
        created.push(id)
        draft.elements.push({
          ...structuredClone(source),
          id,
          groupId: source.groupId ? groupMap.get(source.groupId) : undefined,
          name: `${source.name} copy`,
          x: source.x + 2,
          y: source.y + 2,
        })
      }
    })
    selectIds(created)
  }

  return {
    project: null,
    activeTab: 'design',
    selectedElementId: null,
    selectedElementIds: [],
    selectedRowId: null,
    dirty: false,
    past: [],
    future: [],
    clipboard: [],
    setProject: (project) => {
      const migrated = project ? migrateProject(project) : null
      set({
        project: migrated ? snapshot(migrated) : null,
        selectedElementId: null,
        selectedElementIds: [],
        selectedRowId: migrated?.rows[0]?.id ?? null,
        activeTab: 'design',
        dirty: false,
        past: [],
        future: [],
        clipboard: [],
      })
    },
    setActiveTab: (activeTab) => set({ activeTab }),
    setSelectedElement: (id) => {
      if (!id) selectIds([])
      else {
        const state = get()
        const element = state.project?.elements.find((candidate) => candidate.id === id)
        const ids = element?.groupId
          ? state.project?.elements.filter((candidate) => candidate.groupId === element.groupId).map((candidate) => candidate.id) ?? [id]
          : [id]
        selectIds(ids)
      }
    },
    setSelectedElements: selectIds,
    selectElement: (id, additive = false) => {
      const state = get()
      const element = state.project?.elements.find((candidate) => candidate.id === id)
      const targetIds = element?.groupId
        ? state.project?.elements.filter((candidate) => candidate.groupId === element.groupId).map((candidate) => candidate.id) ?? [id]
        : [id]
      if (!additive) return selectIds(targetIds)
      const current = new Set(state.selectedElementIds)
      const removing = targetIds.every((target) => current.has(target))
      targetIds.forEach((target) => removing ? current.delete(target) : current.add(target))
      selectIds([...current])
    },
    setSelectedRow: (selectedRowId) => set({ selectedRowId }),
    markSaved: () => set({ dirty: false }),
    renameProject: (name) => mutate((draft) => void (draft.name = name)),
    updateCard: (patch) => mutate((draft) => Object.assign(draft.card, patch)),
    updateDataSettings: (patch) => mutate((draft) => Object.assign(draft.dataSettings, patch)),
    updateRender: (patch) => mutate((draft) => Object.assign(draft.render, patch)),
    addElement: (type) => {
      const id = newId('element')
      mutate((draft) => {
        const element = createElement(type, draft.elements.length)
        element.id = id
        draft.elements.push(element)
      })
      selectIds([id])
    },
    addImageElement: (assetId, name) => {
      const id = newId('element')
      mutate((draft) => {
        const element = createImageElement(assetId, name)
        element.id = id
        draft.elements.push(element)
      })
      selectIds([id])
    },
    updateElement: (id, patch) => mutate((draft) => { const element = draft.elements.find((candidate) => candidate.id === id); if (element) Object.assign(element, patch) }),
    updateElements: (patches) => mutate((draft) => { for (const element of draft.elements) if (patches[element.id]) Object.assign(element, patches[element.id]) }),
    deleteElement: (id) => {
      mutate((draft) => { draft.elements = draft.elements.filter((element) => element.id !== id); removeUnusedGroups(draft) })
      const ids = get().selectedElementIds.filter((selected) => selected !== id)
      selectIds(ids)
    },
    deleteSelected: () => {
      const deleted = [...get().selectedElementIds]
      if (!deleted.length) return
      mutate((draft) => { draft.elements = draft.elements.filter((element) => !deleted.includes(element.id)); removeUnusedGroups(draft) })
      selectIds([])
    },
    duplicateElement: (id) => duplicateIds([id]),
    duplicateSelected: () => { const ids = get().selectedElementIds; if (ids.length) duplicateIds(ids) },
    copySelected: () => set((state) => ({ clipboard: state.project?.elements.filter((element) => state.selectedElementIds.includes(element.id)).map((element) => structuredClone(element)) ?? [] })),
    pasteClipboard: () => {
      const copied = get().clipboard.map((element) => structuredClone(element))
      if (!copied.length) return
      const created: string[] = []
      mutate((draft) => {
        const groupMap = new Map<string, string>()
        for (const source of copied) {
          if (source.groupId && !groupMap.has(source.groupId)) {
            const id = newId('group')
            groupMap.set(source.groupId, id)
            draft.groups.push({ id, name: 'Pasted group' })
          }
          const id = newId('element')
          created.push(id)
          draft.elements.push({ ...source, id, groupId: source.groupId ? groupMap.get(source.groupId) : undefined, x: source.x + 3, y: source.y + 3 })
        }
      })
      selectIds(created)
    },
    moveElement: (id, direction) => mutate((draft) => {
      const index = draft.elements.findIndex((element) => element.id === id)
      const target = index + direction
      if (index < 0 || target < 0 || target >= draft.elements.length) return
      const [element] = draft.elements.splice(index, 1)
      draft.elements.splice(target, 0, element)
    }),
    reorderElement: (sourceId, targetId, position) => mutate((draft) => {
      const sourceIndex = draft.elements.findIndex((element) => element.id === sourceId)
      if (sourceIndex < 0 || sourceId === targetId) return
      const [element] = draft.elements.splice(sourceIndex, 1)
      const targetIndex = draft.elements.findIndex((candidate) => candidate.id === targetId)
      if (targetIndex < 0) return
      const insertionIndex = position === 'before' ? targetIndex + 1 : targetIndex
      draft.elements.splice(insertionIndex, 0, element)
    }),
    groupSelected: () => {
      const ids = get().selectedElementIds
      if (ids.length < 2) return
      mutate((draft) => {
        const group: ElementGroup = { id: newId('group'), name: `Group ${draft.groups.length + 1}` }
        draft.groups.push(group)
        for (const element of draft.elements) if (ids.includes(element.id)) element.groupId = group.id
        removeUnusedGroups(draft)
      })
    },
    ungroupSelected: () => {
      const ids = get().selectedElementIds
      if (!ids.length) return
      mutate((draft) => { for (const element of draft.elements) if (ids.includes(element.id)) delete element.groupId; removeUnusedGroups(draft) })
    },
    alignSelected: (mode) => {
      const ids = get().selectedElementIds
      if (!ids.length) return
      mutate((draft) => {
        const elements = draft.elements.filter((element) => ids.includes(element.id) && !element.locked)
        if (!elements.length) return
        const bounds = elements.length === 1
          ? { left: 0, right: draft.card.widthMm, top: 0, bottom: draft.card.heightMm }
          : { left: Math.min(...elements.map((element) => element.x)), right: Math.max(...elements.map((element) => element.x + element.width)), top: Math.min(...elements.map((element) => element.y)), bottom: Math.max(...elements.map((element) => element.y + element.height)) }
        for (const element of elements) {
          if (mode === 'left') element.x = bounds.left
          if (mode === 'center-x') element.x = (bounds.left + bounds.right - element.width) / 2
          if (mode === 'right') element.x = bounds.right - element.width
          if (mode === 'top') element.y = bounds.top
          if (mode === 'center-y') element.y = (bounds.top + bounds.bottom - element.height) / 2
          if (mode === 'bottom') element.y = bounds.bottom - element.height
        }
      })
    },
    distributeSelected: (mode) => {
      const ids = get().selectedElementIds
      if (ids.length < 3) return
      mutate((draft) => {
        const elements = draft.elements.filter((element) => ids.includes(element.id) && !element.locked)
        const horizontal = mode === 'horizontal'
        elements.sort((a, b) => horizontal ? a.x + a.width / 2 - b.x - b.width / 2 : a.y + a.height / 2 - b.y - b.height / 2)
        if (elements.length < 3) return
        const first = elements[0]
        const last = elements.at(-1)!
        const start = horizontal ? first.x + first.width / 2 : first.y + first.height / 2
        const end = horizontal ? last.x + last.width / 2 : last.y + last.height / 2
        const step = (end - start) / (elements.length - 1)
        elements.slice(1, -1).forEach((element, index) => {
          if (horizontal) element.x = start + step * (index + 1) - element.width / 2
          else element.y = start + step * (index + 1) - element.height / 2
        })
      })
    },
    nudgeSelected: (dx, dy) => {
      const ids = get().selectedElementIds
      if (!ids.length) return
      mutate((draft) => { for (const element of draft.elements) if (ids.includes(element.id) && !element.locked) { element.x += dx; element.y += dy } })
    },
    addVariable: (type, name, suppliedDefault) => {
      const id = newId('variable')
      mutate((draft) => {
        const count = draft.variables.filter((variable) => variable.type === type).length + 1
        const defaultValue = suppliedDefault ?? (type === 'number' ? 0 : type === 'color' ? '#E66B3C' : type === 'boolean' ? 0 : '')
        draft.variables.push({ id, name: name?.trim() || `${type[0].toUpperCase()}${type.slice(1)} ${count}`, type, defaultValue, columnWidthPx: defaultVariableWidth(type) })
        for (const row of draft.rows) row.values[id] = defaultValue
      })
      return id
    },
    updateVariable: (id, patch) => mutate((draft) => { const variable = draft.variables.find((candidate) => candidate.id === id); if (variable) Object.assign(variable, patch) }),
    deleteVariable: (id, suppliedReplacement) => mutate((draft) => {
      const variable = draft.variables.find((candidate) => candidate.id === id)
      const replacement = variable
        ? coerceGridValue(suppliedReplacement ?? variable.defaultValue, variable, draft.assets)
        : suppliedReplacement
      draft.variables = draft.variables.filter((variable) => variable.id !== id)
      for (const row of draft.rows) delete row.values[id]
      for (const element of draft.elements) {
        for (const [property, variableId] of Object.entries(element.bindings)) {
          if (variableId !== id) continue
          if (replacement !== undefined) applyBindingConstant(element, property, replacement)
          delete element.bindings[property]
        }
      }
    }),
    addRow: () => {
      const id = newId('card')
      mutate((draft) => draft.rows.push({ id, name: `Card ${draft.rows.length + 1}`, values: valuesForVariables(draft.variables) }))
      set({ selectedRowId: id })
    },
    updateRow: (id, patch) => mutate((draft) => { const row = draft.rows.find((candidate) => candidate.id === id); if (row) Object.assign(row, patch) }),
    updateCell: (rowId, variableId, value) => mutate((draft) => { const row = draft.rows.find((candidate) => candidate.id === rowId); if (row) row.values[variableId] = value }),
    setImageCell: (rowId, variableId, assetId, asset) => mutate((draft) => {
      const row = draft.rows.find((candidate) => candidate.id === rowId)
      const variable = draft.variables.find((candidate) => candidate.id === variableId)
      if (!row || variable?.type !== 'image') return
      if (asset) draft.assets[asset.id] = asset
      row.values[variableId] = assetId
    }),
    duplicateRow: (id) => {
      const nextId = newId('card')
      mutate((draft) => {
        const index = draft.rows.findIndex((row) => row.id === id)
        if (index < 0) return
        const source = draft.rows[index]
        draft.rows.splice(index + 1, 0, { id: nextId, name: `${source.name} copy`, values: structuredClone(source.values) })
      })
      set({ selectedRowId: nextId })
    },
    duplicateRows: (ids) => {
      const selected = new Set(ids)
      const sources = get().project?.rows.filter((row) => selected.has(row.id)) ?? []
      if (!sources.length) return []
      const created = new Map(sources.map((row) => [row.id, newId('card')]))
      mutate((draft) => {
        draft.rows = draft.rows.flatMap((row) => {
          const id = created.get(row.id)
          return id ? [row, { id, name: `${row.name} copy`, values: structuredClone(row.values) }] : [row]
        })
      })
      const createdIds = [...created.values()]
      set({ selectedRowId: createdIds[0] })
      return createdIds
    },
    deleteRow: (id) => {
      mutate((draft) => void (draft.rows = draft.rows.filter((row) => row.id !== id)))
      set((state) => ({ selectedRowId: state.project?.rows.find((row) => row.id !== id)?.id ?? null }))
    },
    deleteRows: (ids) => {
      const selected = new Set(ids)
      const project = get().project
      const removable = project?.rows.filter((row) => selected.has(row.id)).map((row) => row.id) ?? []
      if (!project || !removable.length || project.rows.length - removable.length < 1) return
      mutate((draft) => { draft.rows = draft.rows.filter((row) => !selected.has(row.id)) })
      set({ selectedRowId: get().project?.rows[0]?.id ?? null })
    },
    reorderRow: (sourceId, targetId, position) => mutate((draft) => {
      const sourceIndex = draft.rows.findIndex((row) => row.id === sourceId)
      if (sourceIndex < 0 || sourceId === targetId) return
      const [row] = draft.rows.splice(sourceIndex, 1)
      const targetIndex = draft.rows.findIndex((candidate) => candidate.id === targetId)
      if (targetIndex < 0) return
      draft.rows.splice(position === 'before' ? targetIndex : targetIndex + 1, 0, row)
    }),
    setRowOrder: (ids) => mutate((draft) => {
      const positions = new Map(ids.map((id, index) => [id, index]))
      draft.rows.sort((left, right) => (positions.get(left.id) ?? Number.MAX_SAFE_INTEGER) - (positions.get(right.id) ?? Number.MAX_SAFE_INTEGER))
    }),
    replaceData: (variables, rows) => mutate((draft) => { draft.variables = variables; draft.rows = rows }),
    importData: (commit) => mutate((draft) => {
      for (const asset of commit.assets) draft.assets[asset.id] = asset
      for (const variable of commit.variables) if (!draft.variables.some((candidate) => candidate.id === variable.id)) draft.variables.push(variable)
      const defaults = valuesForVariables(draft.variables)
      const rows = commit.rows.map((row) => ({ ...row, values: { ...defaults, ...row.values } }))
      if (commit.mode === 'append') draft.rows.push(...rows)
      else draft.rows = rows
    }),
    pasteGrid: (startRow, startColumn, cells) => mutate((draft) => {
      while (draft.rows.length < startRow + cells.length) draft.rows.push({ id: newId('card'), name: `Card ${draft.rows.length + 1}`, values: valuesForVariables(draft.variables) })
      cells.forEach((sourceRow, rowOffset) => {
        const target = draft.rows[startRow + rowOffset]
        sourceRow.forEach((cell, columnOffset) => {
          const column = startColumn + columnOffset
          if (column <= draft.variables.length) assignGridValue(draft, target, column, cell)
        })
      })
    }),
    pasteGridRows: (rowIds, startColumn, cells, appendRows = false) => {
      const applied = appendRows ? cells.length : Math.min(rowIds.length, cells.length)
      if (!applied) return 0
      mutate((draft) => {
        for (let rowOffset = 0; rowOffset < applied; rowOffset += 1) {
          let target = draft.rows.find((row) => row.id === rowIds[rowOffset])
          if (!target && appendRows) {
            target = { id: newId('card'), name: `Card ${draft.rows.length + 1}`, values: valuesForVariables(draft.variables) }
            draft.rows.push(target)
          }
          if (!target) continue
          cells[rowOffset].forEach((cell, columnOffset) => {
            const column = startColumn + columnOffset
            if (column <= draft.variables.length) assignGridValue(draft, target!, column, cell)
          })
        }
      })
      return applied
    },
    clearGrid: (range) => {
      const project = get().project
      if (!project?.rows.length) return
      mutate((draft) => {
        const normalized = normalizeGridRange(clampGridRange(range, draft.rows.length, draft.variables.length + 1))
        for (let rowIndex = normalized.top; rowIndex <= normalized.bottom; rowIndex += 1) {
          for (let column = normalized.left; column <= normalized.right; column += 1) {
            if (column === 0) draft.rows[rowIndex].name = ''
            else {
              const variable = draft.variables[column - 1]
              if (variable) draft.rows[rowIndex].values[variable.id] = emptyGridValue(variable)
            }
          }
        }
      })
    },
    clearGridRows: (rowIds, left, right) => {
      if (!rowIds.length) return
      mutate((draft) => {
        for (const id of rowIds) {
          const row = draft.rows.find((candidate) => candidate.id === id)
          if (!row) continue
          for (let column = left; column <= right; column += 1) {
            if (column === 0) row.name = ''
            else {
              const variable = draft.variables[column - 1]
              if (variable) row.values[variable.id] = emptyGridValue(variable)
            }
          }
        }
      })
    },
    fillGrid: (range, direction) => {
      const project = get().project
      if (!project?.rows.length) return
      mutate((draft) => {
        const normalized = normalizeGridRange(clampGridRange(range, draft.rows.length, draft.variables.length + 1))
        if (direction === 'down') {
          for (let column = normalized.left; column <= normalized.right; column += 1) {
            const value = gridCellValue(draft, draft.rows[normalized.top], column)
            for (let rowIndex = normalized.top + 1; rowIndex <= normalized.bottom; rowIndex += 1) assignGridValue(draft, draft.rows[rowIndex], column, value)
          }
        } else {
          for (let rowIndex = normalized.top; rowIndex <= normalized.bottom; rowIndex += 1) {
            const value = gridCellValue(draft, draft.rows[rowIndex], normalized.left)
            for (let column = normalized.left + 1; column <= normalized.right; column += 1) assignGridValue(draft, draft.rows[rowIndex], column, value)
          }
        }
      })
    },
    fillGridRows: (rowIds, left, right, direction) => {
      if (!rowIds.length) return
      mutate((draft) => {
        const rows = rowIds.map((id) => draft.rows.find((row) => row.id === id)).filter((row): row is CardRow => Boolean(row))
        if (!rows.length) return
        if (direction === 'down') {
          for (let column = left; column <= right; column += 1) {
            const value = gridCellValue(draft, rows[0], column)
            for (const row of rows.slice(1)) assignGridValue(draft, row, column, value)
          }
        } else {
          for (const row of rows) {
            const value = gridCellValue(draft, row, left)
            for (let column = left + 1; column <= right; column += 1) assignGridValue(draft, row, column, value)
          }
        }
      })
    },
    fillGridSeries: (range, step = 1) => {
      const project = get().project
      if (!project?.rows.length) return
      mutate((draft) => {
        const normalized = normalizeGridRange(clampGridRange(range, draft.rows.length, draft.variables.length + 1))
        if (normalized.left !== normalized.right || normalized.left === 0) return
        const variable = draft.variables[normalized.left - 1]
        if (variable?.type !== 'number') return
        const start = Number(gridCellValue(draft, draft.rows[normalized.top], normalized.left)) || 0
        for (let rowIndex = normalized.top; rowIndex <= normalized.bottom; rowIndex += 1) {
          draft.rows[rowIndex].values[variable.id] = start + (rowIndex - normalized.top) * step
        }
      })
    },
    fillGridSeriesRows: (rowIds, column, step = 1) => {
      if (!rowIds.length || column === 0) return
      mutate((draft) => {
        const variable = draft.variables[column - 1]
        if (variable?.type !== 'number') return
        const rows = rowIds.map((id) => draft.rows.find((row) => row.id === id)).filter((row): row is CardRow => Boolean(row))
        if (!rows.length) return
        const start = Number(gridCellValue(draft, rows[0], column)) || 0
        rows.forEach((row, index) => { row.values[variable.id] = start + index * step })
      })
    },
    addAsset: (asset) => mutate((draft) => void (draft.assets[asset.id] = asset)),
    addFontFamilies: (families) => mutate((draft) => {
      for (const supplied of families) {
        const existing = Object.values(draft.fonts).find((family) => family.name.trim().toLocaleLowerCase() === supplied.name.trim().toLocaleLowerCase())
        if (existing) existing.faces.push(...supplied.faces)
        else draft.fonts[supplied.id] = supplied
      }
    }),
    deleteFontFamily: (id) => mutate((draft) => void (delete draft.fonts[id])),
    deleteFontFace: (familyId, faceId) => mutate((draft) => {
      const family = draft.fonts[familyId]
      if (!family) return
      family.faces = family.faces.filter((face) => face.id !== faceId)
      if (!family.faces.length) delete draft.fonts[familyId]
    }),
    undo: () => set((state) => {
      const previous = state.past.at(-1)
      if (!previous || !state.project) return state
      return { ...state, project: snapshot(previous), past: state.past.slice(0, -1), future: [snapshot(state.project), ...state.future].slice(0, 50), dirty: true }
    }),
    redo: () => set((state) => {
      const next = state.future[0]
      if (!next || !state.project) return state
      return { ...state, project: snapshot(next), past: [...state.past, snapshot(state.project)].slice(-50), future: state.future.slice(1), dirty: true }
    }),
  }
})
